import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../shared/modal";
import { RoomTypeSelect } from "./room-type-select";

/**
 * "CREATE STORY" / "CREATE PROJECT" / "ADD VIDEO": the naming step the designs put in front of
 * every create. For a project it also asks for the room, which is where the design has the member
 * choose it.
 *
 * WHY THIS DOES NOT CALL THE API. Naming a thing is not saving it — MemberProfileRules refuses a
 * story with no images and a project with no pair, so a "create" that posted a bare title would be
 * rejected by the server every time. The modal collects the title (and room), hands them to the
 * editor through the URL, and the editor saves the whole item once there is one to save. That is
 * also what the designs show: pressing Create lands you on the edit page with the title already in
 * the heading.
 *
 * The title is not validated here either. TitleMaxLength lives in configuration and the server
 * enforces it; the input carries maxLength so the member cannot quietly exceed it, and the only
 * gate is that the field is not empty, because sending a blank name would just bounce. The room is
 * required only when there are rooms to offer — every zone but projects gets an empty list.
 */
export function AddItemModal({ newItemUrl, titleMaxLength, roomTypes = [], copy = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [roomTypeKey, setRoomTypeKey] = useState("");

  const asksForRoom = roomTypes.length > 0;
  const canCreate = title.trim().length > 0 && (!asksForRoom || roomTypeKey !== "");

  const create = (event) => {
    event.preventDefault();

    if (!canCreate) {
      return;
    }

    // newItemUrl already carries the `new` flag, hence the ampersand.
    const room = asksForRoom ? `&roomType=${encodeURIComponent(roomTypeKey)}` : "";

    window.location.assign(`${newItemUrl}&title=${encodeURIComponent(title.trim())}${room}`);
  };

  return (
    <>
      <button
        type="button"
        className="button member-profile-zone__add"
        data-testid="zone-add"
        onClick={() => setIsOpen(true)}
      >
        {copy.addLabel}
        <svg className="icon" aria-hidden="true">
          <use href="#sprite-plus"></use>
        </svg>
      </button>

      <Dialog
        open={isOpen}
        onOpenChange={(next) => {
          setIsOpen(next);

          // A cancelled modal should not leave a half-typed name behind for the next time it is
          // opened.
          if (!next) {
            setTitle("");
            setRoomTypeKey("");
          }
        }}
      >
        {/* The dialog has a title and labelled fields and needs no separate description; passing
            undefined is Radix's documented way of saying so rather than leaving a console warning
            on every open. */}
        <DialogContent
          className="member-profile-add"
          data-testid="zone-add-modal"
          aria-describedby={undefined}
        >
          <DialogTitle className="member-profile-add__title">{copy.modalTitle}</DialogTitle>

          <form className="member-profile-add__form" onSubmit={create}>
            {/*
              A notched outline, as the design draws it: the label sits on the border rather than
              above the field. It is a real <label>, so it is still the input's accessible name -
              the notch is decoration, not a substitute.
            */}
            <div className="member-profile-add__field">
              <label className="member-profile-add__label" htmlFor="zone-add-title">
                {copy.titleLabel}
              </label>

              <input
                id="zone-add-title"
                type="text"
                className="member-profile-add__input"
                data-testid="zone-add-title"
                maxLength={titleMaxLength}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                // The modal exists to collect this value, so it should be ready to receive it.
                autoFocus
              />
            </div>

            {asksForRoom ? (
              <div className="member-profile-add__select">
                {/* The design shows only the placeholder; the label is for screen readers. */}
                <span className="visually-hidden" id="zone-add-room-type-label">
                  {copy.roomTypeLabel}
                </span>

                <RoomTypeSelect
                  roomTypes={roomTypes}
                  value={roomTypeKey}
                  onChange={setRoomTypeKey}
                  placeholder={copy.roomTypePlaceholder}
                  labelledBy="zone-add-room-type-label"
                  testId="zone-add-room-type"
                />
              </div>
            ) : null}

            <div className="member-profile-add__footer">
              <button
                type="button"
                className="button transparent member-profile-add__cancel"
                data-testid="zone-add-cancel"
                onClick={() => setIsOpen(false)}
              >
                {copy.cancel}
              </button>

              <button
                type="submit"
                className="button grey member-profile-add__confirm"
                data-testid="zone-add-confirm"
                disabled={!canCreate}
              >
                {copy.confirm}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
